import { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Link, useNavigate } from "react-router-dom";
import {
  FiGrid,
  FiRefreshCw,
  FiAlertCircle,
  FiChevronDown,
  FiPlay,
  FiLayers,
  FiCheck,
  FiBarChart2,
  FiClock,
  FiArchive,
} from "react-icons/fi";
import { FaCrown } from "react-icons/fa";
import {
  getCurriculumConfig,
  REQUEST_CATEGORY_CONFIG,
  getRequestCategoryConfig,
  REQUEST_MODIFIER_CONFIG,
} from "../utils/curriculumConfig";
import CustomDropdown from "../components/common/CustomDropdown";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import GeneratorBenchmarkTable from "../components/dashboard/GeneratorBenchmarkTable";
import {
  fetchDashboardData,
  fetchAllGenerators,
  processRunsWithBenchmarkSets,
  buildMatrixData,
  buildMatrixDataBestRun,
} from "../services/dashboardService";
import {
  fetchLatestSnapshot,
  fetchSnapshot,
} from "../services/snapshotService";
import { compareGrades } from "../utils/gradeSort";

// Custom Tooltip content for bar chart - shows actual pass rate with decimals on hover
const ChartTooltipContent = ({ active, payload, label }) => {
  if (!active || !payload?.length) {
    return null;
  }
  const items = payload.map((entry) => {
    const { name, value } = entry;
    const actualPassRate = entry.payload?.actualPassRate;
    const displayValue =
      name === "Pass Rate" &&
      actualPassRate !== null &&
      actualPassRate !== undefined
        ? `${actualPassRate.toFixed(2)}%`
        : value;
    return { name, value: displayValue, color: entry.color };
  });
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-md">
      <p className="mb-1 text-sm font-medium text-gray-700">{label}</p>
      {items.map((item) => (
        <p key={item.name} className="text-sm" style={{ color: item.color }}>
          {item.name}: {item.value}
        </p>
      ))}
    </div>
  );
};
ChartTooltipContent.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      color: PropTypes.string,
      payload: PropTypes.shape({
        actualPassRate: PropTypes.number,
      }),
    })
  ),
  label: PropTypes.string,
};

// View mode options
const VIEW_MODES = {
  RECENT: "recent", // Last 7 days + fallback
  BEST: "best", // Best run (highest pass rate)
};

const normalizeDimension = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ");

const canonicalCurriculum = (value) => {
  const normalized = normalizeDimension(value);
  const compact = normalized.replace(/\s+/g, "");
  const aliases = {
    cc: "cc",
    commoncore: "cc",
    teks: "teks",
    ap: "ap",
    sat: "sat",
    athenacc: "athenacc",
  };
  return aliases[compact] || compact;
};

const canonicalSubject = (value) => {
  const normalized = normalizeDimension(value);
  const compact = normalized.replace(/\s+/g, "");
  const aliases = {
    ela: "language",
    englishlanguagearts: "language",
    languagearts: "language",
    language: "language",
    math: "math",
    mathematics: "math",
    science: "science",
    socialstudies: "socialstudies",
    reading: "reading",
    readingandwriting: "readingandwriting",
    apush: "apush",
    apeh: "apeh",
    apeuro: "apeh",
    apeuropeanhistory: "apeh",
    apwh: "apwh",
    chemistry: "chemistry",
    apchemistry: "chemistry",
  };
  return aliases[compact] || compact;
};

const formatConfiguredGrade = (grade) => {
  const value = String(grade);
  return value === "Kindergarten" || value === "k" ? "K" : value;
};

const getBenchmarkSetsFromRuns = (runs) => {
  const benchmarkSetsMap = new Map();
  runs.forEach((run) => {
    run.benchmarkSets?.forEach((bs) => {
      const key = `${bs.subject}_${bs.grade}`;
      if (!benchmarkSetsMap.has(key)) {
        benchmarkSetsMap.set(key, {
          subject: bs.subject,
          grade: String(bs.grade),
        });
      }
    });
  });

  return Array.from(benchmarkSetsMap.values()).sort((a, b) => {
    if (a.subject !== b.subject) {
      return a.subject.localeCompare(b.subject);
    }
    return compareGrades(a.grade, b.grade);
  });
};

const getBenchmarkSetsFromSnapshot = (snapshot, curriculum, subject) => {
  if (!snapshot?.config || !curriculum || !subject) {
    return [];
  }

  const targetCurriculum = canonicalCurriculum(curriculum);
  const targetSubject = canonicalSubject(subject);
  const grades = new Set();

  Object.entries(snapshot.config).forEach(([key, rows]) => {
    const [configCurriculum, configSubject] = key.split("/");
    if (
      canonicalCurriculum(configCurriculum) !== targetCurriculum ||
      canonicalSubject(configSubject) !== targetSubject
    ) {
      return;
    }

    rows.forEach((row) => {
      row.grades?.forEach((grade) => {
        grades.add(formatConfiguredGrade(grade));
      });
    });
  });

  return Array.from(grades)
    .sort(compareGrades)
    .map((grade) => ({ subject, grade }));
};

const getBenchmarkSetsFromSnapshotCurriculum = (snapshot, curriculum) => {
  if (!snapshot?.config || !curriculum) {
    return [];
  }

  const targetCurriculum = canonicalCurriculum(curriculum);
  const benchmarkSets = [];

  Object.entries(snapshot.config).forEach(([key, rows]) => {
    const [configCurriculum, configSubject] = key.split("/");
    if (canonicalCurriculum(configCurriculum) !== targetCurriculum) {
      return;
    }

    rows.forEach((row) => {
      row.grades?.forEach((grade) => {
        benchmarkSets.push({
          subject: configSubject,
          grade: formatConfiguredGrade(grade),
        });
      });
    });
  });

  return benchmarkSets.sort((a, b) => {
    if (canonicalSubject(a.subject) !== canonicalSubject(b.subject)) {
      return a.subject.localeCompare(b.subject);
    }
    return compareGrades(a.grade, b.grade);
  });
};

const applyRunSubjectAliases = (snapshotSets, runSets) => {
  const runSubjectByCanonical = new Map();
  runSets.forEach((bs) => {
    const key = canonicalSubject(bs.subject);
    if (!runSubjectByCanonical.has(key)) {
      runSubjectByCanonical.set(key, bs.subject);
    }
  });

  return snapshotSets.map((bs) => ({
    ...bs,
    subject:
      runSubjectByCanonical.get(canonicalSubject(bs.subject)) || bs.subject,
  }));
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);
  const [excludedErrorCount, setExcludedErrorCount] = useState(0);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  // View mode toggle
  const [viewMode, setViewMode] = useState(VIEW_MODES.BEST);

  // Data state
  const [generators, setGenerators] = useState([]);
  const [benchmarkSets, setBenchmarkSets] = useState([]);
  const [matrixData, setMatrixData] = useState({});

  // Cache for mode switching (avoid refetching)
  const [allGeneratorsCache, setAllGeneratorsCache] = useState([]);
  const [processedRunsCache, setProcessedRunsCache] = useState([]);
  const [selectedDatasetSnapshot, setSelectedDatasetSnapshot] = useState(null);
  const [latestDatasetSnapshot, setLatestDatasetSnapshot] = useState(null);

  // Filter state for matrix
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedCurriculum, setSelectedCurriculum] = useState("");

  // Detail view state (bottom section) - separate from matrix filter
  const [detailSubject, setDetailSubject] = useState(null);
  const [detailGrade, setDetailGrade] = useState(null);
  const [chartMetric, setChartMetric] = useState("passRate"); // "passRate" or "aggregateScore"

  // Request category filter (null means "all")
  const [selectedRequestCategory, setSelectedRequestCategory] =
    useState("question");
  // Modifier filters (false = off, true = filter to matching runs)
  const [selectedHasInterests, setSelectedHasInterests] = useState(false);
  const [selectedHasStimulus, setSelectedHasStimulus] = useState(false);

  // Version filters (null means "all")
  const [selectedInceptVersion, setSelectedInceptVersion] = useState(null);
  const [selectedDatasetVersion, setSelectedDatasetVersion] = useState(null);
  const [selectedCurriculumVersion, setSelectedCurriculumVersion] =
    useState(null);

  // Latest InceptBench version from API (for older-version indicator)
  const [latestApiInceptVersion, setLatestApiInceptVersion] = useState(null);

  // Build matrix from cached data based on current view mode
  const rebuildMatrix = useCallback(
    (procRuns, allGenerators, mode) => {
      const buildFn =
        mode === VIEW_MODES.BEST ? buildMatrixDataBestRun : buildMatrixData;
      const {
        matrixData: matrix,
        generators: gens,
        benchmarkSets: bmSets,
      } = buildFn(procRuns, allGenerators);

      setGenerators(gens);
      setBenchmarkSets(bmSets);
      setMatrixData(matrix);

      // Initialize the subject once. The subject validity effect below owns
      // curriculum/category changes so modifier filters do not move subjects.
      if (bmSets.length > 0) {
        const subjects = [...new Set(bmSets.map((bs) => bs.subject))];
        const defaultSubject = subjects.includes("ela") ? "ela" : subjects[0];

        // Use functional updates to avoid dependency on current state
        setSelectedSubject((prev) => prev ?? defaultSubject);
        setDetailSubject((prev) => {
          if (prev) {
            return prev;
          }
          const gradesForSubject = bmSets
            .filter((bs) => bs.subject === defaultSubject)
            .map((bs) => bs.grade);
          if (gradesForSubject.length > 0) {
            setDetailGrade(gradesForSubject[0]);
          }
          return defaultSubject;
        });
      }
    },
    [] // No dependencies needed - uses functional state updates
  );

  const loadDashboardData = useCallback(
    async (isInitialLoad = true) => {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }
      setError(null);

      try {
        // Step 1: Fetch all generators, lightweight run data, AND latest API version in parallel
        const [
          allGenerators,
          { runs, usingFallback: fallback, excludedErrorCount: errCount },
          versionsRes,
        ] = await Promise.all([
          fetchAllGenerators(),
          fetchDashboardData(),
          fetch("https://api.inceptbench.com/versions")
            .then((r) => r.json())
            .catch(() => null),
        ]);

        if (versionsRes?.current) {
          setLatestApiInceptVersion(versionsRes.current);
        }

        setUsingFallback(fallback);
        setExcludedErrorCount(errCount || 0);

        // Even if no runs, we still want to show generators
        if (allGenerators.length === 0 && (!runs || runs.length === 0)) {
          setGenerators([]);
          setBenchmarkSets([]);
          setMatrixData({});
          setAllGeneratorsCache([]);
          setProcessedRunsCache([]);
          return;
        }

        // Step 2: Process runs to resolve benchmark sets (smart derivation)
        const procRuns =
          runs && runs.length > 0
            ? await processRunsWithBenchmarkSets(runs)
            : [];

        // Cache for mode switching
        setAllGeneratorsCache(allGenerators);
        setProcessedRunsCache(procRuns);

        // Step 3: Build matrix data structure based on current mode
        rebuildMatrix(procRuns, allGenerators, viewMode);

        setLastRefreshed(new Date());
      } catch (err) {
        console.error("Error loading dashboard data:", err);
        setError("Failed to load dashboard data. Please try again.");
      } finally {
        setLoading(false);
        setIsRefreshing(false);
      }
    },
    [rebuildMatrix, viewMode]
  );

  // Refresh handler (doesn't show full loading screen)
  const handleRefresh = useCallback(() => {
    loadDashboardData(false);
  }, [loadDashboardData]);

  useEffect(() => {
    loadDashboardData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  useEffect(() => {
    let isCurrent = true;

    const loadDatasetSnapshots = async () => {
      const latestPromise = fetchLatestSnapshot();
      const selectedPromise = selectedDatasetVersion
        ? fetchSnapshot(selectedDatasetVersion)
        : Promise.resolve({ data: null, error: null });

      const [latestResult, selectedResult] = await Promise.all([
        latestPromise,
        selectedPromise,
      ]);

      if (!isCurrent) {
        return;
      }

      setLatestDatasetSnapshot(latestResult.error ? null : latestResult.data);
      setSelectedDatasetSnapshot(
        selectedDatasetVersion && !selectedResult.error
          ? selectedResult.data
          : null
      );
    };

    loadDatasetSnapshots();

    return () => {
      isCurrent = false;
    };
  }, [selectedDatasetVersion]);

  // Get unique curricula from cached runs (data-driven — no hardcoded entries)
  const availableCurricula = useMemo(() => {
    const curricula = new Set();
    processedRunsCache.forEach((run) => {
      const curriculum = run.filters?.curriculum;
      if (curriculum) {
        curricula.add(curriculum);
      }
    });
    return Array.from(curricula).sort();
  }, [processedRunsCache]);

  // Default the curriculum filter to Common Core if available; otherwise
  // fall back to the first available option once runs load.
  useEffect(() => {
    if (selectedCurriculum || availableCurricula.length === 0) {
      return;
    }
    const ccOption = availableCurricula.find(
      (c) => c.toLowerCase() === "cc" || c.toLowerCase() === "common core"
    );
    setSelectedCurriculum(ccOption || availableCurricula[0]);
  }, [availableCurricula, selectedCurriculum]);

  // Get unique InceptBench versions from runs, always including the latest API version
  const availableInceptVersions = useMemo(() => {
    const versions = new Set();
    processedRunsCache.forEach((run) => {
      if (run.inceptbench_version) {
        versions.add(run.inceptbench_version);
      }
    });
    // Always include the latest API version even if no runs exist for it
    if (latestApiInceptVersion) {
      versions.add(latestApiInceptVersion);
    }
    return Array.from(versions).sort((a, b) => {
      const aParts = a.split(".").map(Number);
      const bParts = b.split(".").map(Number);
      for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
        const aPart = aParts[i] || 0;
        const bPart = bParts[i] || 0;
        if (bPart !== aPart) {
          return bPart - aPart;
        }
      }
      return 0;
    }); // Descending semver
  }, [processedRunsCache, latestApiInceptVersion]);

  // Get unique Dataset versions from runs
  const availableDatasetVersions = useMemo(() => {
    const versions = new Set();
    processedRunsCache.forEach((run) => {
      if (run.dataset_version) {
        versions.add(String(run.dataset_version));
      }
    });
    return Array.from(versions).sort((a, b) => {
      const aParts = a.split(".").map(Number);
      const bParts = b.split(".").map(Number);
      for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
        const aPart = aParts[i] || 0;
        const bPart = bParts[i] || 0;
        if (bPart !== aPart) {
          return bPart - aPart;
        }
      }
      return 0;
    }); // Descending semver
  }, [processedRunsCache]);

  // Get unique Curriculum versions from runs
  const availableCurriculumVersions = useMemo(() => {
    const versions = new Set();
    processedRunsCache.forEach((run) => {
      if (run.curriculum_version) {
        versions.add(run.curriculum_version);
      }
    });
    return Array.from(versions).sort((a, b) => {
      const aParts = a.split(".").map(Number);
      const bParts = b.split(".").map(Number);
      for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
        const aPart = aParts[i] || 0;
        const bPart = bParts[i] || 0;
        if (bPart !== aPart) {
          return bPart - aPart;
        }
      }
      return 0;
    }); // Descending semver
  }, [processedRunsCache]);

  // Filter runs by curriculum and versions
  const filteredRuns = useMemo(() => {
    let runs = processedRunsCache;

    // Filter by curriculum
    if (selectedCurriculum) {
      runs = runs.filter(
        (run) =>
          run.filters?.curriculum?.toLowerCase() ===
          selectedCurriculum.toLowerCase()
      );
    }

    // Filter by InceptBench version
    if (selectedInceptVersion) {
      runs = runs.filter(
        (run) => run.inceptbench_version === selectedInceptVersion
      );
    }

    // Filter by Dataset version
    if (selectedDatasetVersion) {
      runs = runs.filter(
        (run) => String(run.dataset_version) === selectedDatasetVersion
      );
    }

    // Filter by Curriculum version
    if (selectedCurriculumVersion) {
      runs = runs.filter(
        (run) => run.curriculum_version === selectedCurriculumVersion
      );
    }

    // Filter by request category
    if (selectedRequestCategory) {
      runs = runs.filter(
        (run) => run.filters?.request_category === selectedRequestCategory
      );
    }

    // Filter by modifier flags
    if (selectedHasInterests) {
      runs = runs.filter((run) => Boolean(run.filters?.has_interests));
    }
    if (selectedHasStimulus) {
      runs = runs.filter((run) => Boolean(run.filters?.has_stimulus));
    }

    return runs;
  }, [
    processedRunsCache,
    selectedCurriculum,
    selectedInceptVersion,
    selectedDatasetVersion,
    selectedCurriculumVersion,
    selectedRequestCategory,
    selectedHasInterests,
    selectedHasStimulus,
  ]);

  const subjectOptionRuns = useMemo(() => {
    let runs = processedRunsCache;

    if (selectedCurriculum) {
      runs = runs.filter(
        (run) =>
          run.filters?.curriculum?.toLowerCase() ===
          selectedCurriculum.toLowerCase()
      );
    }
    if (selectedInceptVersion) {
      runs = runs.filter(
        (run) => run.inceptbench_version === selectedInceptVersion
      );
    }
    if (selectedDatasetVersion) {
      runs = runs.filter(
        (run) => String(run.dataset_version) === selectedDatasetVersion
      );
    }
    if (selectedCurriculumVersion) {
      runs = runs.filter(
        (run) => run.curriculum_version === selectedCurriculumVersion
      );
    }
    if (selectedRequestCategory) {
      runs = runs.filter(
        (run) => run.filters?.request_category === selectedRequestCategory
      );
    }

    return runs;
  }, [
    processedRunsCache,
    selectedCurriculum,
    selectedInceptVersion,
    selectedDatasetVersion,
    selectedCurriculumVersion,
    selectedRequestCategory,
  ]);

  // Rebuild matrix when view mode or filters change (without refetching)
  useEffect(() => {
    if (filteredRuns.length > 0 && allGeneratorsCache.length > 0) {
      rebuildMatrix(filteredRuns, allGeneratorsCache, viewMode);
    } else if (allGeneratorsCache.length > 0) {
      // Reset if no runs match filters
      setGenerators(allGeneratorsCache);
      setBenchmarkSets([]);
      setMatrixData({});
    }
  }, [viewMode, filteredRuns, allGeneratorsCache, rebuildMatrix]);

  const fallbackBenchmarkSets = useMemo(
    () => getBenchmarkSetsFromRuns(subjectOptionRuns),
    [subjectOptionRuns]
  );

  const configuredSubjectBenchmarkSets = useMemo(() => {
    const selectedSnapshotSets = getBenchmarkSetsFromSnapshotCurriculum(
      selectedDatasetSnapshot,
      selectedCurriculum
    );
    if (selectedSnapshotSets.length > 0) {
      return applyRunSubjectAliases(
        selectedSnapshotSets,
        fallbackBenchmarkSets
      );
    }

    const latestSnapshotSets = getBenchmarkSetsFromSnapshotCurriculum(
      latestDatasetSnapshot,
      selectedCurriculum
    );
    if (latestSnapshotSets.length > 0) {
      return applyRunSubjectAliases(latestSnapshotSets, fallbackBenchmarkSets);
    }

    return [];
  }, [
    fallbackBenchmarkSets,
    latestDatasetSnapshot,
    selectedCurriculum,
    selectedDatasetSnapshot,
  ]);

  const subjectBenchmarkSets =
    configuredSubjectBenchmarkSets.length > 0
      ? configuredSubjectBenchmarkSets
      : fallbackBenchmarkSets;

  const getConfiguredBenchmarkSetsForSubject = useCallback(
    (subject) => {
      const selectedSnapshotSets = getBenchmarkSetsFromSnapshot(
        selectedDatasetSnapshot,
        selectedCurriculum,
        subject
      );
      if (selectedSnapshotSets.length > 0) {
        return selectedSnapshotSets;
      }

      const latestSnapshotSets = getBenchmarkSetsFromSnapshot(
        latestDatasetSnapshot,
        selectedCurriculum,
        subject
      );
      if (latestSnapshotSets.length > 0) {
        return latestSnapshotSets;
      }

      return fallbackBenchmarkSets.filter((bs) => bs.subject === subject);
    },
    [
      fallbackBenchmarkSets,
      latestDatasetSnapshot,
      selectedCurriculum,
      selectedDatasetSnapshot,
    ]
  );

  // Get unique subjects for dropdown
  const subjects = useMemo(() => {
    return [...new Set(subjectBenchmarkSets.map((bs) => bs.subject))].sort();
  }, [subjectBenchmarkSets]);

  useEffect(() => {
    if (subjects.length === 0) {
      return;
    }

    const defaultSubject = subjects.includes("ela") ? "ela" : subjects[0];
    setSelectedSubject((prev) =>
      prev && subjects.includes(prev) ? prev : defaultSubject
    );
    setDetailSubject((prev) =>
      prev && subjects.includes(prev) ? prev : defaultSubject
    );
  }, [subjects]);

  // Filter benchmark sets by selected subject
  const filteredBenchmarkSets = useMemo(() => {
    if (!selectedSubject) {
      return benchmarkSets;
    }
    return getConfiguredBenchmarkSetsForSubject(selectedSubject);
  }, [benchmarkSets, getConfiguredBenchmarkSetsForSubject, selectedSubject]);

  // Calculate coverage ratio for each generator (used to show/hide generators without data)
  const filteredGeneratorStats = useMemo(() => {
    const totalBenchmarkSets = filteredBenchmarkSets.length;
    const stats = {};

    generators.forEach((gen) => {
      const genData = matrixData[gen.id] || {};
      let coveredCount = 0;

      filteredBenchmarkSets.forEach((bs) => {
        const bmKey = `${bs.subject}_${bs.grade}`;
        if (genData[bmKey]) {
          coveredCount++;
        }
      });

      stats[gen.id] = {
        coverageRatio:
          totalBenchmarkSets > 0 ? coveredCount / totalBenchmarkSets : 0,
      };
    });

    return stats;
  }, [generators, matrixData, filteredBenchmarkSets]);

  // Format subject for display
  const formatSubject = (s) => s?.charAt(0).toUpperCase() + s?.slice(1);

  const selectedModifierLabel = selectedHasInterests
    ? "Interests"
    : selectedHasStimulus
      ? "Stimulus"
      : null;

  // Get grades for detail view based on detail subject
  const detailGrades = useMemo(() => {
    if (!detailSubject) {
      return [];
    }
    return getConfiguredBenchmarkSetsForSubject(detailSubject)
      .map((bs) => bs.grade)
      .sort(compareGrades);
  }, [detailSubject, getConfiguredBenchmarkSetsForSubject]);

  // When detail subject changes, reset grade to first available
  useEffect(() => {
    if (detailGrades.length > 0 && !detailGrades.includes(detailGrade)) {
      setDetailGrade(detailGrades[0]);
    }
  }, [detailGrades, detailGrade]);

  // Compute ranking data for detail view
  const detailRankingData = useMemo(() => {
    if (!detailSubject || !detailGrade) {
      return [];
    }
    const bmKey = `${detailSubject}_${detailGrade}`;

    // Count total runs per generator for this benchmark
    const runCounts = {};
    processedRunsCache.forEach((run) => {
      const hasBenchmark = run.benchmarkSets?.some(
        (bs) =>
          bs.subject === detailSubject &&
          String(bs.grade) === String(detailGrade)
      );
      if (hasBenchmark) {
        runCounts[run.generator_id] = (runCounts[run.generator_id] || 0) + 1;
      }
    });

    // Only include generators with data for this benchmark
    return generators
      .map((gen) => {
        const cellData = matrixData[gen.id]?.[bmKey];
        return {
          id: gen.id,
          name: gen.name,
          passRate: cellData?.displayPassRate ?? null,
          actualPassRate:
            cellData?.passRate !== null && cellData?.passRate !== undefined
              ? cellData.passRate * 100
              : null,
          aggregateScore: cellData?.displayScore ?? null,
          totalRuns: runCounts[gen.id] || 0,
          hasData:
            cellData?.displayPassRate !== null &&
            cellData?.displayPassRate !== undefined,
          itemsEvaluated: cellData?.itemsEvaluated || 0,
          isUsingFallback: cellData?.isUsingFallback || false,
          inceptbenchVersion: cellData?.inceptbenchVersion || null,
          curriculumVersion: cellData?.curriculumVersion || null,
          datasetVersion: cellData?.datasetVersion || null,
          runDate: cellData?.runDate || null,
          runId: cellData?.runId || null,
          isStatic: cellData?.isStatic || false,
          varietyScore: cellData?.varietyScore ?? null,
        };
      })
      .filter((gen) => gen.hasData) // Only show generators with data
      .sort((a, b) => {
        // Static generators sort to bottom
        if (a.isStatic !== b.isStatic) {
          return a.isStatic ? 1 : -1;
        }
        // Sort by pass rate descending
        if (a.passRate !== b.passRate) {
          return (b.passRate ?? 0) - (a.passRate ?? 0);
        }
        // Tie-breaker: aggregate score descending
        if (a.aggregateScore !== b.aggregateScore) {
          return (b.aggregateScore ?? 0) - (a.aggregateScore ?? 0);
        }
        // Tie-breaker: more total runs ranks higher
        if (a.totalRuns !== b.totalRuns) {
          return b.totalRuns - a.totalRuns;
        }
        // Finally alphabetically
        return a.name.localeCompare(b.name);
      });
  }, [detailSubject, detailGrade, generators, matrixData, processedRunsCache]);

  // Chart data for detail view (detailRankingData already filters for hasData)
  const detailChartData = useMemo(() => {
    if (!detailSubject || !detailGrade) {
      return [];
    }
    const bmKey = `${detailSubject}_${detailGrade}`;

    return detailRankingData.map((g) => {
      const cellData = matrixData[g.id]?.[bmKey];
      return {
        name: g.name,
        "Pass Rate": g.passRate,
        actualPassRate: g.actualPassRate,
        "Aggregate Score": g.aggregateScore ? Math.round(g.aggregateScore) : 0,
        runId: cellData?.runId, // Include runId for navigation
      };
    });
  }, [detailRankingData, detailSubject, detailGrade, matrixData]);

  // Format "Data synced X ago" text
  const formatLastRefreshed = () => {
    if (!lastRefreshed) {
      return "";
    }

    const seconds = Math.floor((new Date() - lastRefreshed) / 1000);
    if (seconds < 60) {
      return "just now";
    }
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return `${minutes}m ago`;
    }
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          {/* Header - always visible */}
          <div className="mb-8">
            <div className="mb-2 flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-lg bg-primary-100">
                <FiGrid className="size-5 text-primary-600" />
              </div>
              <h1 className="text-4xl font-semibold text-gray-900">
                Dashboard
              </h1>
            </div>
            <p className="mt-3 text-lg text-gray-600">
              Compare generator performance across benchmarks.
            </p>
          </div>

          {/* Loading Spinner in card */}
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="mx-auto mb-4 size-12 animate-spin rounded-full border-y-2 border-primary-600"></div>
                <p className="text-sm text-gray-500">Loading dashboard...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="flex min-h-[60vh] items-center justify-center">
            <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center">
              <p className="text-lg font-medium text-red-800">{error}</p>
              <button
                onClick={loadDashboardData}
                className="mt-4 rounded-lg bg-red-600 px-6 py-2 text-sm font-medium text-white transition-all hover:bg-red-700"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Empty state - show table if we have generators OR benchmark sets
  const hasData = generators.length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="mb-2 flex items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-lg bg-primary-100">
                  <FiGrid className="size-5 text-primary-600" />
                </div>
                <h1 className="text-4xl font-semibold text-gray-900">
                  Dashboard
                </h1>
              </div>
              <p className="mt-3 text-lg text-gray-600">
                Compare generator performance across benchmarks.
              </p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-600 shadow-sm transition-all hover:border-gray-400 hover:bg-gray-50 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FiRefreshCw
                  className={`size-5 ${isRefreshing ? "animate-spin" : ""}`}
                />
                <span>Refresh</span>
              </button>
              {lastRefreshed && (
                <span className="text-xs text-gray-400">
                  Synced {formatLastRefreshed()}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* View Mode Toggle */}
        <div className="mb-6 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-3 rounded-full bg-gray-100 p-1">
            <button
              onClick={() => setViewMode(VIEW_MODES.RECENT)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-all ${
                viewMode === VIEW_MODES.RECENT
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Recent Activity
            </button>
            <button
              onClick={() => setViewMode(VIEW_MODES.BEST)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-all ${
                viewMode === VIEW_MODES.BEST
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Best Performance
            </button>
          </div>
          <span className="text-sm text-gray-400">—</span>
          <p className="text-sm text-gray-500">
            {viewMode === VIEW_MODES.RECENT
              ? "From last 7 days (older data if no recent runs)"
              : "Highest ever achieved"}
          </p>

          {/* Version Filters */}
          <div className="ml-auto flex items-center gap-3">
            {/* InceptBench Version */}
            {availableInceptVersions.length > 0 && (
              <CustomDropdown
                options={[
                  { value: "", label: "All Evaluators" },
                  ...availableInceptVersions.map((v) => {
                    const isLatest = v === latestApiInceptVersion;
                    return {
                      value: v,
                      // Pad label for measurement so dropdown is wide enough for the "Latest" badge
                      label: isLatest
                        ? `InceptBench v${v}     Latest`
                        : `InceptBench v${v}`,
                      displayLabel: `InceptBench v${v}`,
                      isLatest,
                    };
                  }),
                ]}
                value={selectedInceptVersion || ""}
                onChange={(v) => setSelectedInceptVersion(v || null)}
                renderTrigger={(selected, isOpen, minWidth) => (
                  <div
                    className="flex items-center justify-between gap-2 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-gray-400"
                    style={{ minWidth }}
                  >
                    <span>
                      {selected?.displayLabel ||
                        selected?.label ||
                        "All Evaluators"}
                    </span>
                    <FiChevronDown
                      className={`size-3.5 shrink-0 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    />
                  </div>
                )}
                renderOption={(option, isSelected) => (
                  <div
                    className={`flex items-center gap-2 whitespace-nowrap px-4 py-2 transition-colors hover:bg-gray-50 ${
                      isSelected
                        ? "bg-gray-50 font-medium text-gray-900"
                        : "text-gray-700"
                    }`}
                  >
                    <span className="flex-1 text-sm">
                      {option.displayLabel || option.label}
                    </span>
                    {option.isLatest && (
                      <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-emerald-700">
                        Latest
                      </span>
                    )}
                    {isSelected && (
                      <FiCheck className="size-4 shrink-0 text-primary-600" />
                    )}
                  </div>
                )}
              />
            )}

            {/* Dataset Version */}
            {availableDatasetVersions.length > 0 && (
              <CustomDropdown
                options={[
                  { value: "", label: "All Datasets" },
                  ...availableDatasetVersions.map((v, idx) => {
                    const isLatest = idx === 0; // Sorted descending, first is latest
                    return {
                      value: v,
                      label: isLatest
                        ? `Dataset v${v}     Latest`
                        : `Dataset v${v}`,
                      displayLabel: `Dataset v${v}`,
                      isLatest,
                    };
                  }),
                ]}
                value={selectedDatasetVersion || ""}
                onChange={(v) => setSelectedDatasetVersion(v || null)}
                renderTrigger={(selected, isOpen, minWidth) => (
                  <div
                    className="flex items-center justify-between gap-2 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-gray-400"
                    style={{ minWidth }}
                  >
                    <span>
                      {selected?.displayLabel ||
                        selected?.label ||
                        "All Datasets"}
                    </span>
                    <FiChevronDown
                      className={`size-3.5 shrink-0 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    />
                  </div>
                )}
                renderOption={(option, isSelected) => (
                  <div
                    className={`flex items-center gap-2 whitespace-nowrap px-4 py-2 transition-colors hover:bg-gray-50 ${
                      isSelected
                        ? "bg-gray-50 font-medium text-gray-900"
                        : "text-gray-700"
                    }`}
                  >
                    <span className="flex-1 text-sm">
                      {option.displayLabel || option.label}
                    </span>
                    {option.isLatest && (
                      <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-emerald-700">
                        Latest
                      </span>
                    )}
                    {isSelected && (
                      <FiCheck className="size-4 shrink-0 text-primary-600" />
                    )}
                  </div>
                )}
              />
            )}

            {/* Curriculum Version */}
            {availableCurriculumVersions.length > 0 && (
              <CustomDropdown
                options={[
                  { value: "", label: "All Curr. Versions" },
                  ...availableCurriculumVersions.map((v, idx) => {
                    const isLatest = idx === 0; // Sorted descending, first is latest
                    return {
                      value: v,
                      label: isLatest
                        ? `Curriculum v${v}     Latest`
                        : `Curriculum v${v}`,
                      displayLabel: `Curriculum v${v}`,
                      isLatest,
                    };
                  }),
                ]}
                value={selectedCurriculumVersion || ""}
                onChange={(v) => setSelectedCurriculumVersion(v || null)}
                renderTrigger={(selected, isOpen, minWidth) => (
                  <div
                    className="flex items-center justify-between gap-2 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-gray-400"
                    style={{ minWidth }}
                  >
                    <span>
                      {selected?.displayLabel ||
                        selected?.label ||
                        "All Curr. Versions"}
                    </span>
                    <FiChevronDown
                      className={`size-3.5 shrink-0 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    />
                  </div>
                )}
                renderOption={(option, isSelected) => (
                  <div
                    className={`flex items-center gap-2 whitespace-nowrap px-4 py-2 transition-colors hover:bg-gray-50 ${
                      isSelected
                        ? "bg-gray-50 font-medium text-gray-900"
                        : "text-gray-700"
                    }`}
                  >
                    <span className="flex-1 text-sm">
                      {option.displayLabel || option.label}
                    </span>
                    {option.isLatest && (
                      <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-amber-700">
                        Latest
                      </span>
                    )}
                    {isSelected && (
                      <FiCheck className="size-4 shrink-0 text-primary-600" />
                    )}
                  </div>
                )}
              />
            )}
          </div>
        </div>

        {/* Excluded Errors Banner */}
        {excludedErrorCount > 0 && (
          <div className="mb-6 flex items-center gap-3 rounded-lg border border-amber-200/80 bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-3">
            <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-amber-100">
              <FiAlertCircle className="size-3.5 text-amber-600" />
            </div>
            <p className="text-sm text-amber-900">
              Runs with errors are excluded from this view. Re-run the affected
              benchmarks to see updated results here.
            </p>
          </div>
        )}

        {/* Fallback Banner (only in recent mode) */}
        {viewMode === VIEW_MODES.RECENT && usingFallback && (
          <div className="mb-6 flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            <FiAlertCircle className="size-4 shrink-0" />
            <span>
              No runs in last 7 days - showing latest available results.
            </span>
          </div>
        )}

        {/* Empty State with Actionable Guidance */}
        {!hasData && (
          <div className="rounded-xl border border-gray-200 bg-white p-12 text-center">
            <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-primary-50">
              <FiGrid className="size-8 text-primary-500" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              No benchmark data yet
            </h2>
            <p className="mx-auto mt-2 max-w-md text-gray-500">
              Run your first benchmark to see performance metrics, comparisons,
              and insights for your content generators.
            </p>
            <div className="mt-6 flex items-center justify-center gap-4">
              <Link
                to="/run"
                className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-6 py-3 text-sm font-medium text-white shadow-sm transition-all hover:bg-primary-700"
              >
                <FiPlay className="size-4" />
                Run Your First Benchmark
              </Link>
              <Link
                to="/benchmark-sets"
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-6 py-3 text-sm font-medium text-gray-700 transition-all hover:bg-gray-50"
              >
                Explore Datasets
              </Link>
            </div>
          </div>
        )}

        {/* Dashboard Content */}
        {hasData && (
          <>
            {/* Performance Matrix Card */}
            <div className="mb-8 rounded-2xl border border-gray-200 bg-white shadow-sm">
              {/* Card Header */}
              <div className="flex flex-wrap items-center justify-between gap-4 rounded-t-2xl border-b border-gray-100 bg-gray-50 px-6 py-4">
                <div className="flex items-center gap-2">
                  <FiLayers className="size-5 text-gray-500" />
                  <h2 className="text-lg font-semibold text-gray-900">
                    Performance Matrix
                  </h2>
                </div>
                {/* Filters */}
                <div className="flex items-center gap-4">
                  {/* Curriculum Dropdown */}
                  {availableCurricula.length > 0 && (
                    <CustomDropdown
                      options={availableCurricula.map((curriculum) => {
                        const config = getCurriculumConfig(curriculum);
                        return {
                          value: curriculum,
                          label: config.abbreviation,
                          config,
                        };
                      })}
                      value={selectedCurriculum}
                      onChange={setSelectedCurriculum}
                      renderTrigger={(selected, isOpen, minWidth) => {
                        const config = getCurriculumConfig(selected?.value);
                        return (
                          <div
                            className={`flex items-center justify-between gap-2 rounded-full border px-4 py-1.5 text-sm font-medium shadow-sm transition-colors ${config.bgColor} ${config.color} ${config.borderColor}`}
                            style={{ minWidth }}
                          >
                            <span>{selected?.label || "CC"}</span>
                            <FiChevronDown
                              className={`size-3.5 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""} ${config.color}`}
                            />
                          </div>
                        );
                      }}
                      renderOption={(option, isSelected) => {
                        const { config } = option;
                        return (
                          <div
                            className={`flex items-center justify-between gap-2 px-3 py-2 transition-colors hover:bg-gray-50 ${
                              isSelected ? "bg-gray-50" : ""
                            }`}
                          >
                            <span
                              className={`rounded-full px-2.5 py-0.5 text-sm font-medium ${config.bgColor} ${config.color}`}
                            >
                              {option.label}
                            </span>
                            {isSelected && (
                              <FiCheck className="size-4 text-primary-600" />
                            )}
                          </div>
                        );
                      }}
                    />
                  )}
                  {/* Subject Dropdown */}
                  {subjects.length > 0 && (
                    <CustomDropdown
                      options={subjects.map((subject) => ({
                        value: subject,
                        label: formatSubject(subject),
                      }))}
                      value={selectedSubject}
                      onChange={(value) => {
                        setSelectedSubject(value);
                      }}
                      renderTrigger={(selected, isOpen, minWidth) => (
                        <div
                          className="flex items-center justify-between gap-2 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-gray-400"
                          style={{ minWidth }}
                        >
                          <span>{selected?.label || "Select Subject"}</span>
                          <FiChevronDown
                            className={`size-3.5 shrink-0 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
                          />
                        </div>
                      )}
                      renderOption={(option, isSelected) => (
                        <div
                          className={`flex items-center justify-between gap-2 whitespace-nowrap px-4 py-2 transition-colors hover:bg-gray-50 ${
                            isSelected
                              ? "bg-gray-50 font-medium text-gray-900"
                              : "text-gray-700"
                          }`}
                        >
                          <span className="flex-1 text-sm">{option.label}</span>
                          {isSelected && (
                            <FiCheck className="size-4 shrink-0 text-primary-600" />
                          )}
                        </div>
                      )}
                    />
                  )}

                  {/* Request Type & Modifier Filters */}
                  <div className="flex items-center gap-2 border-l border-gray-200 pl-4">
                    <CustomDropdown
                      options={[
                        { value: "", label: "All Types" },
                        ...Object.entries(REQUEST_CATEGORY_CONFIG).map(
                          ([key, config]) => ({
                            value: key,
                            label: config.label,
                            config,
                          })
                        ),
                      ]}
                      value={selectedRequestCategory || ""}
                      onChange={(v) => setSelectedRequestCategory(v || null)}
                      renderTrigger={(selected, isOpen, minWidth) => {
                        const config = selected?.value
                          ? getRequestCategoryConfig(selected.value)
                          : null;
                        return (
                          <div
                            className={`flex items-center justify-between gap-2 whitespace-nowrap rounded-lg border px-3 py-1.5 text-sm font-medium shadow-sm transition-colors ${
                              config
                                ? `${config.bgColor} ${config.color} ${config.borderColor}`
                                : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                            }`}
                            style={{ minWidth }}
                          >
                            <span>{selected?.label || "All Types"}</span>
                            <FiChevronDown
                              className={`size-3.5 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""} ${config ? config.color : "text-gray-400"}`}
                            />
                          </div>
                        );
                      }}
                      renderOption={(option, isSelected) => (
                        <div
                          className={`flex items-center justify-between gap-2 whitespace-nowrap px-4 py-2 transition-colors hover:bg-gray-50 ${
                            isSelected
                              ? "bg-gray-50 font-medium text-gray-900"
                              : "text-gray-700"
                          }`}
                        >
                          <span className="flex-1 text-sm">{option.label}</span>
                          {isSelected && (
                            <FiCheck className="size-4 shrink-0 text-primary-600" />
                          )}
                        </div>
                      )}
                    />
                    <label
                      className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg border px-3 py-1.5 text-sm font-medium shadow-sm transition-colors ${
                        selectedHasInterests
                          ? `${REQUEST_MODIFIER_CONFIG.interest.bgColor} ${REQUEST_MODIFIER_CONFIG.interest.color} border-amber-300`
                          : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                      }`}
                      title={
                        selectedHasInterests
                          ? "Remove interests filter"
                          : "Show only runs with interests"
                      }
                    >
                      <input
                        type="checkbox"
                        checked={selectedHasInterests}
                        onChange={(event) => {
                          const next = event.target.checked;
                          setSelectedHasInterests(next);
                          if (next) {
                            setSelectedHasStimulus(false);
                          }
                        }}
                        className="size-4 rounded border-gray-300 accent-amber-600"
                      />
                      Interests
                    </label>
                    <label
                      className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg border px-3 py-1.5 text-sm font-medium shadow-sm transition-colors ${
                        selectedHasStimulus
                          ? `${REQUEST_MODIFIER_CONFIG.stimulus.bgColor} ${REQUEST_MODIFIER_CONFIG.stimulus.color} border-rose-300`
                          : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                      }`}
                      title={
                        selectedHasStimulus
                          ? "Remove stimulus filter"
                          : "Show only runs with stimulus"
                      }
                    >
                      <input
                        type="checkbox"
                        checked={selectedHasStimulus}
                        onChange={(event) => {
                          const next = event.target.checked;
                          setSelectedHasStimulus(next);
                          if (next) {
                            setSelectedHasInterests(false);
                          }
                        }}
                        className="size-4 rounded border-gray-300 accent-rose-600"
                      />
                      Stimulus
                    </label>
                  </div>
                </div>
              </div>
              {/* Card Content */}
              <div className="p-0">
                {selectedModifierLabel &&
                selectedSubject &&
                filteredBenchmarkSets.length === 0 ? (
                  <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
                    <FiAlertCircle className="mb-3 size-8 text-gray-300" />
                    <p className="text-sm font-medium text-gray-700">
                      No {selectedModifierLabel.toLowerCase()} benchmark data
                      for {formatSubject(selectedSubject)}.
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      Clear {selectedModifierLabel} to view the available
                      results for this subject.
                    </p>
                  </div>
                ) : (
                  <GeneratorBenchmarkTable
                    generators={generators}
                    benchmarkSets={filteredBenchmarkSets}
                    matrixData={matrixData}
                    generatorStats={filteredGeneratorStats}
                    latestInceptVersion={latestApiInceptVersion}
                    latestDatasetVersion={availableDatasetVersions[0] || null}
                    latestCurriculumVersion={
                      availableCurriculumVersions[0] || null
                    }
                    curriculum={selectedCurriculum}
                    showOlderDataIndicator={viewMode === VIEW_MODES.RECENT}
                  />
                )}
              </div>
            </div>

            {/* Leaderboard Card */}
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              {/* Card Header */}
              <div className="flex flex-wrap items-center gap-6 border-b border-gray-100 bg-gray-50 px-6 py-4">
                <div className="flex items-center gap-2">
                  <FiBarChart2 className="size-5 text-gray-500" />
                  <h2 className="text-lg font-semibold text-gray-900">
                    Leaderboard
                  </h2>
                </div>
                {/* Filters */}
                <div className="flex flex-wrap items-center gap-4">
                  {/* Curriculum Dropdown */}
                  {availableCurricula.length > 0 && (
                    <CustomDropdown
                      options={availableCurricula.map((curriculum) => {
                        const config = getCurriculumConfig(curriculum);
                        return {
                          value: curriculum,
                          label: config.abbreviation,
                          config,
                        };
                      })}
                      value={selectedCurriculum}
                      onChange={setSelectedCurriculum}
                      renderTrigger={(selected, isOpen, minWidth) => {
                        const config = getCurriculumConfig(selected?.value);
                        return (
                          <div
                            className={`flex items-center justify-between gap-2 rounded-full border px-4 py-1.5 text-sm font-medium shadow-sm transition-colors ${config.bgColor} ${config.color} ${config.borderColor}`}
                            style={{ minWidth }}
                          >
                            <span>{selected?.label || "CC"}</span>
                            <FiChevronDown
                              className={`size-3.5 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""} ${config.color}`}
                            />
                          </div>
                        );
                      }}
                      renderOption={(option, isSelected) => {
                        const { config } = option;
                        return (
                          <div
                            className={`flex items-center justify-between gap-2 px-3 py-2 transition-colors hover:bg-gray-50 ${
                              isSelected ? "bg-gray-50" : ""
                            }`}
                          >
                            <span
                              className={`rounded-full px-2.5 py-0.5 text-sm font-medium ${config.bgColor} ${config.color}`}
                            >
                              {option.label}
                            </span>
                            {isSelected && (
                              <FiCheck className="size-4 text-primary-600" />
                            )}
                          </div>
                        );
                      }}
                    />
                  )}
                  {/* Subject Dropdown */}
                  {subjects.length > 0 && (
                    <CustomDropdown
                      options={subjects.map((subject) => ({
                        value: subject,
                        label: formatSubject(subject),
                      }))}
                      value={detailSubject}
                      onChange={(value) => {
                        setDetailSubject(value);
                        // Reset grade when subject changes
                        const gradesForSubject = benchmarkSets
                          .filter((bs) => bs.subject === value)
                          .map((bs) => bs.grade);
                        if (gradesForSubject.length > 0) {
                          setDetailGrade(gradesForSubject[0]);
                        }
                      }}
                      renderTrigger={(selected, isOpen, minWidth) => (
                        <div
                          className="flex items-center justify-between gap-2 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-gray-400"
                          style={{ minWidth }}
                        >
                          <span>{selected?.label || "Select Subject"}</span>
                          <FiChevronDown
                            className={`size-3.5 shrink-0 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
                          />
                        </div>
                      )}
                      renderOption={(option, isSelected) => (
                        <div
                          className={`flex items-center justify-between gap-2 whitespace-nowrap px-4 py-2 transition-colors hover:bg-gray-50 ${
                            isSelected
                              ? "bg-gray-50 font-medium text-gray-900"
                              : "text-gray-700"
                          }`}
                        >
                          <span className="flex-1 text-sm">{option.label}</span>
                          {isSelected && (
                            <FiCheck className="size-4 shrink-0 text-primary-600" />
                          )}
                        </div>
                      )}
                    />
                  )}
                  {/* Grade Chips */}
                  {detailGrades.length > 0 && (
                    <div className="flex items-center gap-1.5">
                      {detailGrades.map((grade) => (
                        <button
                          key={grade}
                          onClick={() => setDetailGrade(grade)}
                          className={`flex min-w-8 items-center justify-center rounded-lg border px-2.5 py-1 text-sm font-medium transition-all ${
                            detailGrade === grade
                              ? "border-primary-500 bg-primary-50 text-primary-700"
                              : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {grade}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Request Type Filters */}
                  <div className="flex items-center gap-2 border-l border-gray-200 pl-4">
                    <CustomDropdown
                      options={[
                        { value: "", label: "All Types" },
                        ...Object.entries(REQUEST_CATEGORY_CONFIG).map(
                          ([key, config]) => ({
                            value: key,
                            label: config.label,
                            config,
                          })
                        ),
                      ]}
                      value={selectedRequestCategory || ""}
                      onChange={(v) => setSelectedRequestCategory(v || null)}
                      renderTrigger={(selected, isOpen, minWidth) => {
                        const config = selected?.value
                          ? getRequestCategoryConfig(selected.value)
                          : null;
                        return (
                          <div
                            className={`flex items-center justify-between gap-2 whitespace-nowrap rounded-lg border px-3 py-1 text-sm font-medium shadow-sm transition-colors ${
                              config
                                ? `${config.bgColor} ${config.color} ${config.borderColor}`
                                : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                            }`}
                            style={{ minWidth }}
                          >
                            <span>{selected?.label || "All Types"}</span>
                            <FiChevronDown
                              className={`size-3.5 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""} ${config ? config.color : "text-gray-400"}`}
                            />
                          </div>
                        );
                      }}
                      renderOption={(option, isSelected) => (
                        <div
                          className={`flex items-center justify-between gap-2 whitespace-nowrap px-4 py-2 transition-colors hover:bg-gray-50 ${
                            isSelected
                              ? "bg-gray-50 font-medium text-gray-900"
                              : "text-gray-700"
                          }`}
                        >
                          <span className="flex-1 text-sm">{option.label}</span>
                          {isSelected && (
                            <FiCheck className="size-4 shrink-0 text-primary-600" />
                          )}
                        </div>
                      )}
                    />
                    <label
                      className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg border px-2.5 py-1 text-sm font-medium shadow-sm transition-colors ${
                        selectedHasInterests
                          ? `${REQUEST_MODIFIER_CONFIG.interest.bgColor} ${REQUEST_MODIFIER_CONFIG.interest.color} border-amber-300`
                          : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                      }`}
                      title={
                        selectedHasInterests
                          ? "Remove interests filter"
                          : "Show only runs with interests"
                      }
                    >
                      <input
                        type="checkbox"
                        checked={selectedHasInterests}
                        onChange={(event) => {
                          const next = event.target.checked;
                          setSelectedHasInterests(next);
                          if (next) {
                            setSelectedHasStimulus(false);
                          }
                        }}
                        className="size-4 rounded border-gray-300 accent-amber-600"
                      />
                      Interests
                    </label>
                    <label
                      className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg border px-2.5 py-1 text-sm font-medium shadow-sm transition-colors ${
                        selectedHasStimulus
                          ? `${REQUEST_MODIFIER_CONFIG.stimulus.bgColor} ${REQUEST_MODIFIER_CONFIG.stimulus.color} border-rose-300`
                          : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                      }`}
                      title={
                        selectedHasStimulus
                          ? "Remove stimulus filter"
                          : "Show only runs with stimulus"
                      }
                    >
                      <input
                        type="checkbox"
                        checked={selectedHasStimulus}
                        onChange={(event) => {
                          const next = event.target.checked;
                          setSelectedHasStimulus(next);
                          if (next) {
                            setSelectedHasInterests(false);
                          }
                        }}
                        className="size-4 rounded border-gray-300 accent-rose-600"
                      />
                      Stimulus
                    </label>
                  </div>
                </div>
              </div>

              {/* Card Content - Two Column Layout */}
              <div className="grid gap-6 p-6 lg:grid-cols-2">
                {/* Left: Ranking Table */}
                <div className="flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white">
                  <div className="shrink-0 border-b border-gray-100 bg-gray-50 px-4 py-3">
                    <h3 className="text-sm font-semibold text-gray-700">
                      Generator Rankings
                    </h3>
                  </div>
                  <div className="max-h-[340px] divide-y divide-gray-100 overflow-y-auto">
                    {detailRankingData.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <p className="text-sm text-gray-500">
                          {selectedModifierLabel && detailSubject
                            ? `No ${selectedModifierLabel.toLowerCase()} benchmark data for ${formatSubject(detailSubject)}${detailGrade ? ` grade ${detailGrade}` : ""}`
                            : "No generators have run this benchmark yet"}
                        </p>
                        {selectedModifierLabel ? (
                          <p className="mt-1 text-xs text-gray-400">
                            Clear {selectedModifierLabel} to view the available
                            rankings for this subject.
                          </p>
                        ) : (
                          <Link
                            to="/run"
                            className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
                          >
                            <FiPlay className="size-3" />
                            Run a benchmark
                          </Link>
                        )}
                      </div>
                    ) : (
                      detailRankingData.map((gen, index) => {
                        const rank = index + 1;
                        const latestDs = availableDatasetVersions[0] || null;

                        // Build tooltip lines
                        const tooltipLines = [];
                        const dateStr = gen.runDate
                          ? new Date(gen.runDate).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                            })
                          : "";
                        const passRateForTooltip =
                          gen.actualPassRate !== null &&
                          gen.actualPassRate !== undefined
                            ? gen.actualPassRate.toFixed(2)
                            : gen.passRate;
                        tooltipLines.push(
                          `${passRateForTooltip}% · ${gen.itemsEvaluated} items · ${dateStr} · Total ${gen.totalRuns} run${gen.totalRuns !== 1 ? "s" : ""}${viewMode === VIEW_MODES.RECENT && gen.isUsingFallback ? " (older data)" : ""}`
                        );
                        if (latestApiInceptVersion && gen.inceptbenchVersion) {
                          const tag =
                            gen.inceptbenchVersion === latestApiInceptVersion
                              ? "LATEST"
                              : "OLD";
                          tooltipLines.push(
                            `InceptBench v${gen.inceptbenchVersion} (${tag})`
                          );
                        }
                        if (latestDs && gen.datasetVersion) {
                          const tag =
                            gen.datasetVersion === latestDs ? "LATEST" : "OLD";
                          tooltipLines.push(
                            `Dataset v${gen.datasetVersion} (${tag})`
                          );
                        }
                        if (gen.curriculumVersion) {
                          const latestCurr =
                            availableCurriculumVersions[0] || null;
                          const tag =
                            latestCurr && gen.curriculumVersion === latestCurr
                              ? "LATEST"
                              : "OLD";
                          tooltipLines.push(
                            `Curriculum v${gen.curriculumVersion} (${tag})`
                          );
                        }
                        if (
                          gen.varietyScore !== null &&
                          gen.varietyScore !== undefined
                        ) {
                          tooltipLines.push(
                            `Variety score: ${(gen.varietyScore * 100).toFixed(2)}`
                          );
                        }
                        tooltipLines.push("Click to view run details");

                        return (
                          <div
                            key={gen.id}
                            role="button"
                            tabIndex={0}
                            title={tooltipLines.join("\n")}
                            className={`flex cursor-pointer items-center gap-4 px-4 py-3 transition-colors hover:bg-gray-50 ${rank === 1 ? "bg-amber-50/50 hover:bg-amber-50/80" : ""}`}
                            onClick={() => {
                              if (gen.runId) {
                                navigate(`/past-runs/${gen.runId}`);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (
                                (e.key === "Enter" || e.key === " ") &&
                                gen.runId
                              ) {
                                navigate(`/past-runs/${gen.runId}`);
                              }
                            }}
                          >
                            {/* Rank */}
                            <div className="flex size-8 shrink-0 items-center justify-center">
                              {rank === 1 ? (
                                <div className="flex size-8 items-center justify-center rounded-lg bg-amber-400 text-white shadow-sm">
                                  <FaCrown className="size-4" />
                                </div>
                              ) : rank === 2 ? (
                                <div className="flex size-8 items-center justify-center rounded-lg bg-gray-300 text-white shadow-sm">
                                  <span className="text-xs font-bold">2</span>
                                </div>
                              ) : rank === 3 ? (
                                <div className="flex size-8 items-center justify-center rounded-lg bg-amber-600 text-white shadow-sm">
                                  <span className="text-xs font-bold">3</span>
                                </div>
                              ) : (
                                <span className="text-sm font-medium text-gray-400">
                                  {rank}
                                </span>
                              )}
                            </div>

                            {/* Generator Name */}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5">
                                <p className="truncate text-sm font-medium text-gray-900">
                                  {gen.name}
                                </p>
                                {gen.isStatic && (
                                  <span className="inline-flex items-center rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700">
                                    Static
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500">
                                {gen.totalRuns} run
                                {gen.totalRuns !== 1 ? "s" : ""}
                              </p>
                            </div>

                            {/* Indicator Icons */}
                            <div className="flex shrink-0 items-center gap-1.5">
                              {viewMode === VIEW_MODES.RECENT &&
                                gen.isUsingFallback && (
                                  <FiClock className="size-3.5 text-gray-400" />
                                )}
                              {((latestApiInceptVersion &&
                                gen.inceptbenchVersion &&
                                gen.inceptbenchVersion !==
                                  latestApiInceptVersion) ||
                                (latestDs &&
                                  gen.datasetVersion &&
                                  gen.datasetVersion !== latestDs) ||
                                (availableCurriculumVersions[0] &&
                                  gen.curriculumVersion &&
                                  gen.curriculumVersion !==
                                    availableCurriculumVersions[0])) && (
                                <FiArchive className="size-3.5 text-slate-400/70" />
                              )}
                            </div>

                            {/* Pass Rate */}
                            <div className="text-right">
                              <p className="text-lg font-bold text-primary-600">
                                {gen.passRate}%
                              </p>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Right: Bar Chart */}
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                  <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-3">
                    <h3 className="text-sm font-semibold text-gray-700">
                      Performance Comparison
                    </h3>
                    {/* Metric Toggle */}
                    <div className="flex items-center rounded-full bg-gray-200 p-0.5">
                      <button
                        onClick={() => setChartMetric("passRate")}
                        className={`rounded-full px-3 py-1 text-xs font-medium transition-all ${
                          chartMetric === "passRate"
                            ? "bg-white text-gray-900 shadow-sm"
                            : "text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        Pass Rate
                      </button>
                      <button
                        onClick={() => setChartMetric("aggregateScore")}
                        className={`rounded-full px-3 py-1 text-xs font-medium transition-all ${
                          chartMetric === "aggregateScore"
                            ? "bg-white text-gray-900 shadow-sm"
                            : "text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        Agg. Score
                      </button>
                    </div>
                  </div>
                  <div className="p-4 outline-none" tabIndex={-1}>
                    {detailChartData.length === 0 ? (
                      <div className="flex h-64 flex-col items-center justify-center text-center">
                        <FiGrid className="mb-2 size-8 text-gray-300" />
                        <p className="text-sm text-gray-500">
                          {selectedModifierLabel && detailSubject
                            ? `No ${selectedModifierLabel.toLowerCase()} data for ${formatSubject(detailSubject)}${detailGrade ? ` grade ${detailGrade}` : ""}`
                            : "No benchmark data available"}
                        </p>
                        <p className="mt-1 text-xs text-gray-400">
                          {selectedModifierLabel
                            ? `Clear ${selectedModifierLabel} to view the available chart.`
                            : "Run a benchmark to see the comparison chart"}
                        </p>
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={detailChartData}
                          margin={{ top: 20, right: 20, left: 0, bottom: 60 }}
                          layout={
                            detailChartData.length <= 3
                              ? "vertical"
                              : "horizontal"
                          }
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#f0f0f0"
                          />
                          {detailChartData.length <= 3 ? (
                            <>
                              <XAxis
                                type="number"
                                domain={[0, 100]}
                                tick={{ fontSize: 11 }}
                                tickLine={false}
                                axisLine={{ stroke: "#e5e7eb" }}
                              />
                              <YAxis
                                type="category"
                                dataKey="name"
                                tick={{ fontSize: 10 }}
                                tickLine={false}
                                axisLine={{ stroke: "#e5e7eb" }}
                                width={120}
                              />
                            </>
                          ) : (
                            <>
                              <XAxis
                                dataKey="name"
                                tick={{
                                  fontSize: 10,
                                  angle: -35,
                                  textAnchor: "end",
                                }}
                                tickLine={false}
                                axisLine={{ stroke: "#e5e7eb" }}
                                interval={0}
                                height={60}
                              />
                              <YAxis
                                domain={[0, 100]}
                                tick={{ fontSize: 11 }}
                                tickLine={false}
                                axisLine={{ stroke: "#e5e7eb" }}
                              />
                            </>
                          )}
                          <Tooltip
                            contentStyle={{
                              borderRadius: "8px",
                              border: "1px solid #e5e7eb",
                              boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                            }}
                            content={<ChartTooltipContent />}
                          />
                          <Bar
                            dataKey={
                              chartMetric === "passRate"
                                ? "Pass Rate"
                                : "Aggregate Score"
                            }
                            fill={
                              chartMetric === "passRate" ? "#6366f1" : "#10b981"
                            }
                            radius={[4, 4, 0, 0]}
                            cursor="pointer"
                            onClick={(data) => {
                              if (data?.runId) {
                                navigate(`/past-runs/${data.runId}`);
                              }
                            }}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
